import * as Sentry from "@sentry/browser";
import { t } from "i18next";
import type { ApiError, ApiResponse } from "../api";
import api from "../api";

export interface TaskStatusResponse {
  taskStatusUrl?: string;
  taskIds?: Array<string>;
  // properties that are returned by apiWithoutCamelization responses
  task_status_url?: string;
  task_ids?: Array<string>;
}

interface PostDataAndPollResponseOptions<T> {
  queryAgainOnPollSuccess?: boolean;
  queryAgainOnPollSuccessWithTaskId?: boolean;
  queryAgainFn?: (
    taskId?: string
  ) => Promise<ApiResponse<TaskStatusResponse & T>>;
  pollInterval?: number;
}

interface QueryWithPollResponseResponse<T> {
  taskIds?: Array<string>;
  data?: T;
  response?: ApiResponse<TaskStatusResponse & T>;
}

export enum TaskStatus {
  Success = "SUCCESS",
  Pending = "PENDING",
  Failure = "FAILURE",
  Error = "ERROR"
}

export interface TaskResponse {
  status: TaskStatus;
  message?: string;
}

/** Calls queryFunc and polls automatically if a `taskStatusUrl` is returned. Returns result otherwise.
 *
 * If `queryAgainOnPollSuccess` is included in the optional `options`, `queryFunc` will be automatically called again on success.
 *
 * If `queryAgainOnPollSuccessWithTaskId` is included in `options`, `queryFunc` will be automatically called again on success, if a `taskId` was returned in the original response.
 */
export async function queryWithPollResponse<T>(
  queryFunc: (taskId?: string) => Promise<ApiResponse<TaskStatusResponse & T>>,
  options?: PostDataAndPollResponseOptions<T>
): Promise<QueryWithPollResponseResponse<T>> {
  let response: ApiResponse<TaskStatusResponse & T>;

  try {
    response = await queryFunc();
  } catch (error) {
    return Promise.reject(error);
  }

  return new Promise((resolve, reject) => {
    const taskStatusUrl =
      response.data.taskStatusUrl ?? response.data.task_status_url;
    const taskIds = response.data.taskIds ?? response.data.task_ids;

    if (taskStatusUrl) {
      pollTaskStatus(
        taskStatusUrl,
        () => {
          if (options && options.queryAgainOnPollSuccess) {
            const taskId = taskIds?.[0];
            const queryAgainFunction = options.queryAgainFn ?? queryFunc;

            if (options.queryAgainOnPollSuccessWithTaskId && taskId) {
              resolve(
                queryWithPollResponse(() => queryAgainFunction(taskId), options)
              );
            } else {
              resolve(queryWithPollResponse(queryAgainFunction, options));
            }
          } else {
            resolve({
              taskIds
            });
          }
        },
        (error) => {
          reject(error);
        },
        options?.pollInterval
      );
    } else {
      resolve({ data: response.data, response: response });
    }
  });
}

export function pollTaskStatus(
  taskStatusUrl: string,
  onSuccess: () => void,
  onError: (unknown) => void,
  interval = 200
): Promise<void> {
  return api
    .get(taskStatusUrl)
    .then((response: { data: TaskResponse }) => {
      const status = response.data.status;
      if (status === TaskStatus.Success) {
        onSuccess();
      } else if (status === TaskStatus.Pending) {
        setTimeout(() => {
          pollTaskStatus(taskStatusUrl, onSuccess, onError, interval);
        }, interval);
      } else if (status === TaskStatus.Error || status === TaskStatus.Failure) {
        onError({
          response: { status: 500 },
          userFacingErrorMessage:
            response.data.message ?? t("errors.ServerError")
        });
      } else {
        Sentry.captureMessage(
          "Task endpoint responded with an unexpected status: " +
            response.data.status
        );
        onError({
          response: { status: 500 },
          userFacingErrorMessage: t("errors.ServerError")
        });
      }
    })
    .catch((error: ApiError) => {
      // Don't report expected network errors from polling to Sentry
      // These typically occur when users navigate away or lose connection during polling
      const isNetworkError =
        error.code === "ERR_NETWORK" ||
        error.code === "ERR_CANCELED" ||
        error.message === "Network Error" ||
        error.response?.status === 0;

      if (!isNetworkError) {
        // Only report unexpected errors to Sentry
        Sentry.captureException(error);
      }

      onError(error);
    });
}

export async function postDataAndPollResponse<T>(
  postUrl: string,
  data,
  options?: PostDataAndPollResponseOptions<T>
): Promise<QueryWithPollResponseResponse<T>> {
  return queryWithPollResponse<T>(() => api.post(postUrl, data), options);
}

export function getFieldSpecificError(e: ApiError, fieldNames?: string[]) {
  const response = e.response;

  if (response?.status === 400) {
    const errorData = response.data;

    return fieldNames && fieldNames.length > 0
      ? fieldNames
          .reduce<Array<string>>(
            (acc, fieldName) => [
              ...acc,
              errorData?.[fieldName] ? errorData?.[fieldName][0] : ""
            ],
            []
          )
          .join(", ")
      : t("errors.UnknownError");
  }

  return null;
}
